import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import AdminPagination from '../AdminPagination';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import {
  useAcademiaAdminCohorteMutations,
  useAcademiaAdminDomaineMutations,
  useAcademiaAdminDomaines,
  useAcademiaAdminFiliereMutations,
  useAcademiaAdminSalles,
} from '../../../hooks/useAcademiaAdmin';
import {
  fetchFiliereCapaciteSuggestion,
  deleteAdminCohorte,
  deleteAdminDomaine,
  deleteAdminFiliere,
  deleteAdminSalle,
  fetchAdminFiliereTarifs,
  putAdminFiliereTarifs,
} from '../../../services/academiaAdminService';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { slugifyDomaineLabel } from '../../../utils/domaineSlug';
import { isAdministrateur } from '../../../utils/adminAccess';
import {
  COHORTE_STATUT_LABELS,
  COHORTE_STATUTS_MANUELS,
  cohorteStatutAutomatiqueLabel,
  getCohortePhase,
  getManualInscriptionStatut,
  isCohorteStatutAutomatique,
  resolveEffectiveCohorteStatut,
} from '../../../utils/cohorteStatus';
import AcademiaSelect from '../../common/AcademiaSelect';
import DomaineFormationForm from './DomaineFormationForm';
import DomaineFormationListItem from './DomaineFormationListItem';
import FiliereCatalogForm from './FiliereCatalogForm';
import FiliereCatalogListItem from './FiliereCatalogListItem';
import {
  emptyFiliereContenuPedagogique,
  parseFiliereContenuPedagogique,
  type FiliereContenuPedagogique,
} from '../../../utils/filiereContenuPedagogique';
import {
  buildFiliereTarifsDraftFromApi,
  emptyFiliereTarifsDraft,
  filiereTarifsDraftToPayload,
  type FiliereTarifsDraft,
} from '../../../utils/filiereTarifsDraft';

type Filiere = {
  id: string;
  code?: string;
  libelle?: string;
  domaine?: string;
  description?: string;
  urlImage?: string | null;
  ordre?: number;
  estActive?: boolean;
  contenuPedagogique?: unknown;
  _count?: { cohortes?: number; sessions?: number };
};
type CohorteCapacity = {
  capaciteCohorte: number;
  capaciteEffective: number;
  inscrits: number;
  placesRestantes: number;
  estComplete?: boolean;
  estPresquePleine?: boolean;
  sessionsLiees: number;
  capaciteSallesMin: number | null;
};
type Cohorte = {
  id: string;
  idFiliere?: string;
  filiere?: { id?: string; code?: string; libelle?: string };
  code?: string;
  libelle?: string;
  statut?: string;
  dateDebut?: string;
  dateFin?: string;
  capaciteMax?: number;
  _count?: { inscriptions?: number };
  capacity?: CohorteCapacity;
};

function cohorteBelongsToFiliere(cohorte: Cohorte, filiereId: string) {
  return cohorte.idFiliere === filiereId || cohorte.filiere?.id === filiereId;
}

function countCohortesForFiliere(filiereId: string, allCohortes: Cohorte[], fallback = 0) {
  const n = allCohortes.filter((c) => cohorteBelongsToFiliere(c, filiereId)).length;
  return n > 0 ? n : fallback;
}

type Salle = {
  id: string;
  code?: string;
  libelle?: string;
  capacite?: number;
  estActive?: boolean;
  _count?: { sessions?: number };
};

type Domaine = {
  id: string;
  code: string;
  libelle: string;
  slug: string;
  intro?: string | null;
  urlImage?: string | null;
  icon?: string;
  ordre?: number;
  estActive?: boolean;
  filieresCount?: number;
};

type Props = {
  user?: { role?: string } | null;
  filieres: Filiere[];
  cohortes: Cohorte[];
  selectedFiliereId: string;
  onFiliereChange: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
  reloadCohortes: (idFiliere: string) => Promise<void>;
};

const COHORTE_STATUTS = ['OUVERTE', 'FERMEE', 'EN_COURS', 'TERMINEE'] as const;

const COHORTE_STATUT_LABELS_UI: Record<string, string> = {
  OUVERTE: COHORTE_STATUT_LABELS.OUVERTE,
  FERMEE: COHORTE_STATUT_LABELS.FERMEE,
  EN_COURS: COHORTE_STATUT_LABELS.EN_COURS,
  TERMINEE: COHORTE_STATUT_LABELS.TERMINEE,
};

function cohorteStatutsForFiliere(filiereId: string, allCohortes: Cohorte[]) {
  const present = new Set(
    allCohortes
      .filter((c) => cohorteBelongsToFiliere(c, filiereId) && c.statut)
      .map((c) => c.statut as string),
  );
  return COHORTE_STATUTS.filter((s) => present.has(s));
}

type SectionTab = 'catalogue' | 'parcours' | 'domaines' | 'salles';

function defaultCohorteDates() {
  const start = new Date();
  const end = new Date(start);
  end.setMonth(end.getMonth() + 2);
  return {
    dateDebut: start.toISOString().slice(0, 10),
    dateFin: end.toISOString().slice(0, 10),
  };
}

function formatShortDate(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function cohorteStatutClass(statut?: string) {
  if (statut === 'OUVERTE') return 'bg-emerald-100 text-emerald-800';
  if (statut === 'EN_COURS') return 'bg-sky-100 text-sky-800';
  if (statut === 'FERMEE') return 'bg-slate-100 text-slate-600';
  if (statut === 'TERMINEE') return 'bg-violet-100 text-violet-800';
  return 'bg-surface-container-high text-on-surface-variant';
}

function MiniStat({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-outline-variant/15 bg-white px-4 py-3 shadow-sm">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <LucideSymbolIcon name={icon} tone="inherit" size={20} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{label}</p>
        <p className="text-lg font-black text-slate-900">{value}</p>
      </div>
    </div>
  );
}

type TableActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: string;
};

function TableActionButton({ icon, children, className = '', ...rest }: TableActionButtonProps) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-1.5 ${className}`}
      {...rest}
    >
      <LucideSymbolIcon name={icon} tone="inherit" size={14} className="shrink-0" />
      {children}
    </button>
  );
}

function CohorteCard({
  cohorte,
  isEditing,
  editDraft,
  isSaving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditDraftChange,
  deleteAction,
}: {
  cohorte: Cohorte;
  isEditing: boolean;
  editDraft: { statut: string; capaciteMax: string };
  isSaving: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditDraftChange: (draft: { statut: string; capaciteMax: string }) => void;
  deleteAction?: React.ReactNode;
}) {
  const inscrits = cohorte.capacity?.inscrits ?? cohorte._count?.inscriptions ?? 0;
  const phase = getCohortePhase(cohorte.dateDebut, cohorte.dateFin);
  const statutEffectif = resolveEffectiveCohorteStatut(cohorte.dateDebut, cohorte.dateFin, cohorte.statut);
  const statutAuto = isCohorteStatutAutomatique(phase);
  const statutAutoLabel = cohorteStatutAutomatiqueLabel(phase);
  const capaciteEffective = cohorte.capacity?.capaciteEffective;
  const capacite = isEditing
    ? Number(editDraft.capaciteMax) || 1
    : capaciteEffective ?? cohorte.capaciteMax ?? 0;
  const ratio = capacite > 0 ? Math.min(100, Math.round((inscrits / capacite) * 100)) : 0;
  const estComplete =
    cohorte.capacity?.estComplete === true ||
    (cohorte.capacity?.placesRestantes ?? capacite - inscrits) <= 0;

  return (
    <article
      className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition hover:shadow-md ${
        estComplete ? 'border-amber-300 bg-amber-50/30' : 'border-outline-variant/20 hover:border-primary/25'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-bold text-slate-900">{cohorte.libelle || cohorte.code}</h3>
          {cohorte.code ? <p className="mt-0.5 font-mono text-xs text-on-surface-variant">{cohorte.code}</p> : null}
        </div>
        {isEditing ? (
          statutAuto ? (
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cohorteStatutClass(statutEffectif)}`}
              >
                {COHORTE_STATUT_LABELS_UI[statutEffectif] ?? statutEffectif}
              </span>
              {statutAutoLabel ? (
                <span className="max-w-[11rem] text-right text-[10px] text-on-surface-variant">{statutAutoLabel}</span>
              ) : null}
            </div>
          ) : (
            <AcademiaSelect
              fieldSize="sm"
              className="w-[9.5rem] shrink-0"
              value={editDraft.statut}
              onChange={(e) => onEditDraftChange({ ...editDraft, statut: e.target.value })}
            >
              {COHORTE_STATUTS_MANUELS.map((s) => (
                <option key={s} value={s}>
                  {COHORTE_STATUT_LABELS_UI[s] ?? s}
                </option>
              ))}
            </AcademiaSelect>
          )
        ) : (
          <div className="flex shrink-0 flex-col items-end gap-1">
            {estComplete ? (
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-950">
                Complète
              </span>
            ) : null}
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cohorteStatutClass(statutEffectif)}`}>
              {COHORTE_STATUT_LABELS_UI[statutEffectif] ?? statutEffectif ?? '—'}
            </span>
            {statutAutoLabel ? (
              <span className="max-w-[11rem] text-right text-[10px] text-on-surface-variant">{statutAutoLabel}</span>
            ) : null}
          </div>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs font-semibold text-on-surface-variant">Début</dt>
          <dd className="font-medium text-slate-800">{formatShortDate(cohorte.dateDebut)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-on-surface-variant">Fin</dt>
          <dd className="font-medium text-slate-800">{formatShortDate(cohorte.dateFin)}</dd>
        </div>
      </dl>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-on-surface-variant">Places</span>
          <span className="font-bold text-slate-900">
            {inscrits} / {isEditing ? (
              <input
                type="number"
                min={1}
                className="ml-1 w-14 rounded border px-1 py-0.5 text-xs font-bold"
                value={editDraft.capaciteMax}
                onChange={(e) => onEditDraftChange({ ...editDraft, capaciteMax: e.target.value })}
              />
            ) : (
              capacite || '—'
            )}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-container-high">
          <div
            className={`h-full rounded-full transition-all ${estComplete || ratio >= 100 ? 'bg-amber-600' : ratio >= 90 ? 'bg-amber-500' : 'bg-primary'}`}
            style={{ width: `${ratio}%` }}
          />
        </div>
        {cohorte.capacity && !isEditing ? (
          <p className="mt-2 text-[11px] text-on-surface-variant">
            Effective (cohorte × salles) : {cohorte.capacity.capaciteEffective}
            {cohorte.capacity.capaciteSallesMin != null
              ? ` · salle min. ${cohorte.capacity.capaciteSallesMin}`
              : ''}
            {cohorte.capacity.sessionsLiees > 0
              ? ` · ${cohorte.capacity.sessionsLiees} session(s) planifiée(s)`
              : ''}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-outline-variant/10 pt-4">
        {isEditing ? (
          <>
            <TableActionButton
              icon="close"
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-low"
              onClick={onCancelEdit}
            >
              Annuler
            </TableActionButton>
            <TableActionButton
              icon="save"
              disabled={isSaving}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
              onClick={onSaveEdit}
            >
              {isSaving ? '…' : 'Enregistrer'}
            </TableActionButton>
          </>
        ) : (
          <>
            <TableActionButton
              icon="edit"
              className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/10"
              onClick={onStartEdit}
            >
              Modifier
            </TableActionButton>
            {deleteAction}
          </>
        )}
      </div>
    </article>
  );
}

type ParcoursCohortesModalProps = {
  filiere: Filiere;
  domaineLabel: string;
  cohortes: Cohorte[];
  onClose: () => void;
  showCreate: boolean;
  onToggleCreate: () => void;
  newCohorte: {
    code: string;
    libelle: string;
    dateDebut: string;
    dateFin: string;
    capaciteMax: string;
    statut: string;
  };
  onNewCohorteChange: React.Dispatch<
    React.SetStateAction<{
      code: string;
      libelle: string;
      dateDebut: string;
      dateFin: string;
      capaciteMax: string;
      statut: string;
    }>
  >;
  onCreate: (e: React.FormEvent) => void;
  onSuggestCapacite: () => void;
  capSuggestion: string | null;
  isSaving: boolean;
  inputClass: string;
  editingId: string | null;
  editDraft: { statut: string; capaciteMax: string };
  onStartEdit: (c: Cohorte) => void;
  onCancelEdit: () => void;
  onSaveEdit: (id: string) => void;
  onEditDraftChange: (draft: { statut: string; capaciteMax: string }) => void;
  renderCohorteDelete?: (cohorte: Cohorte) => React.ReactNode;
};

function ParcoursCohortesModal({
  filiere,
  domaineLabel,
  cohortes,
  onClose,
  showCreate,
  onToggleCreate,
  newCohorte,
  onNewCohorteChange,
  onCreate,
  onSuggestCapacite,
  capSuggestion,
  isSaving,
  inputClass,
  editingId,
  editDraft,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditDraftChange,
  renderCohorteDelete,
}: ParcoursCohortesModalProps) {
  const ouvertes = cohortes.filter((c) => c.statut === 'OUVERTE').length;
  const {
    paginatedItems: paginatedCohortes,
    currentPage,
    totalPages,
    onPageChange,
    summary: cohortesPaginationSummary,
  } = useAdminTablePagination(cohortes);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200] bg-black/50"
        role="presentation"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="parcours-cohortes-modal-title"
        className="fixed left-1/2 top-1/2 z-[201] flex max-h-[min(92vh,900px)] w-[min(calc(100vw-2rem),56rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-outline-variant/15 bg-surface-container-lowest p-5">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">{domaineLabel}</p>
            <h3 id="parcours-cohortes-modal-title" className="mt-1 truncate text-lg font-black text-primary">
              {filiere.libelle || filiere.code}
            </h3>
            {filiere.code ? (
              <p className="mt-0.5 font-mono text-xs text-on-surface-variant">{filiere.code}</p>
            ) : null}
            <p className="mt-2 text-sm text-on-surface-variant">
              {cohortes.length} cohorte{cohortes.length !== 1 ? 's' : ''}
              {ouvertes > 0 ? ` · ${ouvertes} ouverte${ouvertes > 1 ? 's' : ''}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={isSaving}
              onClick={onToggleCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              <LucideSymbolIcon name={showCreate ? 'close' : 'add'} tone="inherit" size={18} />
              {showCreate ? 'Fermer' : 'Nouvelle cohorte'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high"
              aria-label="Fermer"
            >
              <LucideSymbolIcon name="close" tone="inherit" size={20} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {filiere.description ? (
            <p className="mb-4 rounded-lg border border-outline-variant/10 bg-white px-4 py-3 text-sm text-on-surface-variant">
              {filiere.description}
            </p>
          ) : null}

          {showCreate ? (
            <form
              onSubmit={onCreate}
              className="mb-5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-white p-5"
            >
              <h4 className="text-sm font-black text-slate-900">Créer une cohorte</h4>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <label className="block">
                  <span className="text-xs font-semibold text-on-surface-variant">Code *</span>
                  <input
                    required
                    className={inputClass}
                    value={newCohorte.code}
                    onChange={(e) => onNewCohorteChange((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                    placeholder="COH-2026-01"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs font-semibold text-on-surface-variant">Libellé affiché</span>
                  <input
                    className={inputClass}
                    value={newCohorte.libelle}
                    onChange={(e) => onNewCohorteChange((d) => ({ ...d, libelle: e.target.value }))}
                    placeholder="Session janvier 2026"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-on-surface-variant">Date de début *</span>
                  <input
                    type="date"
                    required
                    className={inputClass}
                    value={newCohorte.dateDebut}
                    onChange={(e) => onNewCohorteChange((d) => ({ ...d, dateDebut: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-on-surface-variant">Date de fin *</span>
                  <input
                    type="date"
                    required
                    className={inputClass}
                    value={newCohorte.dateFin}
                    onChange={(e) => onNewCohorteChange((d) => ({ ...d, dateFin: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-on-surface-variant">Capacité max *</span>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="number"
                      min={1}
                      required
                      className="w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm"
                      value={newCohorte.capaciteMax}
                      onChange={(e) => onNewCohorteChange((d) => ({ ...d, capaciteMax: e.target.value }))}
                    />
                    <button
                      type="button"
                      title="Capacité selon salles et planning"
                      className="shrink-0 rounded-lg border border-primary/30 px-2 text-xs font-bold text-primary"
                      onClick={onSuggestCapacite}
                    >
                      Auto
                    </button>
                  </div>
                  {capSuggestion ? (
                    <p className="mt-1 text-[11px] text-on-surface-variant">{capSuggestion}</p>
                  ) : null}
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-on-surface-variant">Statut inscriptions (avant le début)</span>
                  <AcademiaSelect
                    className="mt-1"
                    value={newCohorte.statut}
                    onChange={(e) => onNewCohorteChange((d) => ({ ...d, statut: e.target.value }))}
                  >
                    {COHORTE_STATUTS_MANUELS.map((s) => (
                      <option key={s} value={s}>
                        {COHORTE_STATUT_LABELS_UI[s] ?? s}
                      </option>
                    ))}
                  </AcademiaSelect>
                  <p className="mt-1 text-[11px] text-on-surface-variant">
                    « En cours » et « Terminée » sont appliqués automatiquement entre la date de début et la date de fin.
                  </p>
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                >
                  {isSaving ? 'Enregistrement…' : 'Créer la cohorte'}
                </button>
              </div>
            </form>
          ) : null}

          {cohortes.length === 0 ? (
            <p className="rounded-xl border border-dashed border-outline-variant/30 bg-white px-6 py-10 text-center text-sm text-on-surface-variant">
              Aucune cohorte pour ce parcours. Cliquez sur « Nouvelle cohorte » pour en ajouter une.
            </p>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {paginatedCohortes.map((c) => (
                  <CohorteCard
                    key={c.id}
                    cohorte={c}
                    isEditing={editingId === c.id}
                    editDraft={editDraft}
                    isSaving={isSaving}
                    onStartEdit={() => onStartEdit(c)}
                    onCancelEdit={onCancelEdit}
                    onSaveEdit={() => void onSaveEdit(c.id)}
                    onEditDraftChange={onEditDraftChange}
                    deleteAction={renderCohorteDelete?.(c)}
                  />
                ))}
              </div>
              <AdminPagination
                className="mt-4 border-t border-outline-variant/15 pt-4"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                summary={cohortesPaginationSummary}
              />
            </>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function AcademiaFormationsPanel({
  user,
  filieres,
  cohortes,
  selectedFiliereId,
  onFiliereChange,
  isLoading,
  isError,
  onRefresh,
  reloadCohortes,
}: Props) {
  const canDelete = isAdministrateur(user);
  const [deletingEntityId, setDeletingEntityId] = useState<string | null>(null);
  const sallesState = useAcademiaAdminSalles();
  const domainesState = useAcademiaAdminDomaines();
  const domaineMutations = useAcademiaAdminDomaineMutations(domainesState.refetch);

  const refreshAll = () => {
    void onRefresh();
    void sallesState.refetch();
    void domainesState.refetch();
  };
  useRegisterPortalRefresh(refreshAll);
  const filiereMutations = useAcademiaAdminFiliereMutations(onRefresh);
  const { createCohorte, updateCohorte, isSaving } = useAcademiaAdminCohorteMutations(
    selectedFiliereId,
    reloadCohortes,
  );

  const dates = defaultCohorteDates();
  type ToastState = { message: string; variant: 'success' | 'error' } | null;
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runAction = useCallback(
    async (action: () => Promise<string | void>, errorFallback = 'Action impossible.') => {
      try {
        const successMessage = await action();
        showToast(successMessage || 'Action réussie.', 'success');
      } catch (e) {
        const message = e instanceof Error ? e.message : errorFallback;
        showToast(message, 'error');
      }
    },
    [showToast],
  );

  const [section, setSection] = useState<SectionTab>('catalogue');
  const [showFiliereForm, setShowFiliereForm] = useState(false);
  const [editingFiliereId, setEditingFiliereId] = useState<string | null>(null);
  const [showDomaineForm, setShowDomaineForm] = useState(false);
  const [editingDomaineId, setEditingDomaineId] = useState<string | null>(null);
  const [newDomaine, setNewDomaine] = useState({
    code: '',
    libelle: '',
    slug: '',
    intro: '',
    urlImage: '',
    icon: 'school',
    ordre: '0',
  });
  const [editDomaine, setEditDomaine] = useState({
    libelle: '',
    slug: '',
    intro: '',
    urlImage: '',
    icon: 'school',
    ordre: '0',
    estActive: true,
  });
  const [newFiliere, setNewFiliere] = useState({
    code: '',
    libelle: '',
    domaine: '',
    description: '',
    urlImage: '',
    ordre: '0',
    contenuPedagogique: emptyFiliereContenuPedagogique(),
    tarifs: emptyFiliereTarifsDraft(),
  });
  const [editFiliere, setEditFiliere] = useState({
    libelle: '',
    domaine: '',
    description: '',
    urlImage: '',
    ordre: '0',
    estActive: true,
    contenuPedagogique: emptyFiliereContenuPedagogique() as FiliereContenuPedagogique,
    tarifs: emptyFiliereTarifsDraft() as FiliereTarifsDraft,
  });
  const [showSalleForm, setShowSalleForm] = useState(false);
  const [editingSalleId, setEditingSalleId] = useState<string | null>(null);
  const [newSalle, setNewSalle] = useState({ code: '', libelle: '', capacite: '20' });
  const [editSalle, setEditSalle] = useState({ libelle: '', capacite: '20', estActive: true });
  const [capSuggestion, setCapSuggestion] = useState<string | null>(null);
  const [newCohorte, setNewCohorte] = useState({
    code: '',
    libelle: '',
    dateDebut: dates.dateDebut,
    dateFin: dates.dateFin,
    capaciteMax: '30',
    statut: 'OUVERTE',
  });
  const [showCreate, setShowCreate] = useState(false);
  const [cohortesModalFiliereId, setCohortesModalFiliereId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({ statut: 'OUVERTE', capaciteMax: '30' });

  const domaines = domainesState.domaines as Domaine[];
  const domaineLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of domaines) {
      map.set(d.code.toUpperCase(), d.libelle);
    }
    return map;
  }, [domaines]);

  const {
    paginatedItems: paginatedDomaines,
    currentPage: domainePage,
    totalPages: domainePages,
    onPageChange: onDomainePageChange,
    summary: domainesPaginationSummary,
  } = useAdminTablePagination(domaines);

  const {
    paginatedItems: paginatedFilieres,
    currentPage: filierePage,
    totalPages: filierePages,
    onPageChange: onFilierePageChange,
    summary: filieresPaginationSummary,
  } = useAdminTablePagination(filieres);

  const {
    paginatedItems: paginatedParcoursRows,
    currentPage: parcoursPage,
    totalPages: parcoursPages,
    onPageChange: onParcoursPageChange,
    summary: parcoursPaginationSummary,
  } = useAdminTablePagination(filieres);

  const {
    paginatedItems: paginatedSalles,
    currentPage: sallePage,
    totalPages: sallePages,
    onPageChange: onSallePageChange,
    summary: sallesSummary,
  } = useAdminTablePagination(sallesState.salles as Salle[]);

  const cohortesOuvertes = cohortes.filter((c) => c.statut === 'OUVERTE').length;
  const totalInscrits = cohortes.reduce((s, c) => s + (c._count?.inscriptions ?? 0), 0);
  const modalFiliereId = cohortesModalFiliereId ?? selectedFiliereId;
  const modalFiliere = filieres.find((f) => f.id === cohortesModalFiliereId);
  const cohortesForModal = useMemo(() => {
    if (!modalFiliereId) return [];
    return cohortes.filter((c) => cohorteBelongsToFiliere(c, modalFiliereId));
  }, [cohortes, modalFiliereId]);

  const openCohortesModal = (id: string) => {
    onFiliereChange(id);
    setCohortesModalFiliereId(id);
    setShowCreate(false);
    setEditingId(null);
    setCapSuggestion(null);
  };

  const closeCohortesModal = () => {
    setCohortesModalFiliereId(null);
    setShowCreate(false);
    setEditingId(null);
    setCapSuggestion(null);
  };

  const sideCard = useMemo(
    () => ({
      label: 'Cohortes ouvertes',
      value: isLoading ? '…' : String(cohortesOuvertes),
      helper: isLoading
        ? null
        : `${domaines.length} domaine(s) · ${filieres.length} filière(s) · ${cohortes.length} cohorte(s)`,
      sideLinks: [
        { label: 'Tarifs', to: '/admin/tarifs', icon: 'payments' },
        { label: 'Inscriptions', to: '/admin/inscriptions', icon: 'assignment' },
      ],
    }),
    [isLoading, cohortesOuvertes, domaines.length, filieres.length, cohortes.length],
  );

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFiliereId) {
      showToast('Sélectionnez une filière pour créer une cohorte.', 'error');
      return;
    }
    void runAction(async () => {
      await createCohorte({
        code: newCohorte.code,
        idFiliere: selectedFiliereId,
        libelle: newCohorte.libelle || newCohorte.code,
        dateDebut: newCohorte.dateDebut,
        dateFin: newCohorte.dateFin,
        capaciteMax: Number(newCohorte.capaciteMax),
        statut: newCohorte.statut,
      });
      setShowCreate(false);
      setNewCohorte({
        code: '',
        libelle: '',
        ...defaultCohorteDates(),
        capaciteMax: '30',
        statut: 'OUVERTE',
      });
      return 'Cohorte créée';
    }, 'Impossible de créer la cohorte');
  };

  const startEdit = (c: Cohorte) => {
    const phase = getCohortePhase(c.dateDebut, c.dateFin);
    setEditingId(c.id);
    setEditDraft({
      statut: phase === 'AVANT' ? getManualInscriptionStatut(c.statut) : resolveEffectiveCohorteStatut(c.dateDebut, c.dateFin, c.statut),
      capaciteMax: String(c.capaciteMax ?? 30),
    });
  };

  const handleSaveEdit = (id: string) => {
    const cohorteRow = cohortes.find((c) => c.id === id);
    const phase = cohorteRow ? getCohortePhase(cohorteRow.dateDebut, cohorteRow.dateFin) : 'AVANT';
    void runAction(async () => {
      await updateCohorte(id, {
        ...(phase === 'AVANT' ? { statut: editDraft.statut } : {}),
        capaciteMax: Number(editDraft.capaciteMax),
      });
      setEditingId(null);
      return 'Cohorte mise à jour';
    }, 'Impossible de mettre à jour la cohorte');
  };

  const handleSuggestCapacite = () => {
    if (!selectedFiliereId) {
      showToast('Sélectionnez une filière.', 'error');
      return;
    }
    void runAction(async () => {
      const s = await fetchFiliereCapaciteSuggestion(selectedFiliereId);
      const max = s?.suggestedCapaciteMax ?? s?.capaciteMaxSuggeree;
      if (max != null) {
        setNewCohorte((d) => ({ ...d, capaciteMax: String(max) }));
        setCapSuggestion(`Capacité suggérée selon les salles du planning : ${max} place(s).`);
        return `Capacité suggérée : ${max} place(s)`;
      }
      setCapSuggestion('Aucune session planifiée pour cette filière — saisie manuelle.');
      return 'Aucune session planifiée — saisie manuelle de la capacité.';
    }, 'Impossible de calculer la suggestion de capacité');
  };

  const handleCreateDomaine = (e: React.FormEvent) => {
    e.preventDefault();
    void runAction(async () => {
      await domaineMutations.createDomaine({
        code: newDomaine.code,
        libelle: newDomaine.libelle,
        slug: newDomaine.slug || slugifyDomaineLabel(newDomaine.libelle),
        intro: newDomaine.intro || undefined,
        urlImage: newDomaine.urlImage || undefined,
        icon: newDomaine.icon || 'school',
        ordre: Number(newDomaine.ordre) || 0,
      });
      setShowDomaineForm(false);
      setNewDomaine({ code: '', libelle: '', slug: '', intro: '', urlImage: '', icon: 'school', ordre: '0' });
      return 'Domaine créé';
    }, 'Impossible de créer le domaine');
  };

  const startEditDomaine = (d: Domaine) => {
    setShowDomaineForm(false);
    setEditingDomaineId(d.id);
    setEditDomaine({
      libelle: d.libelle,
      slug: d.slug,
      intro: d.intro ?? '',
      urlImage: d.urlImage ?? '',
      icon: d.icon ?? 'school',
      ordre: String(d.ordre ?? 0),
      estActive: d.estActive !== false,
    });
  };

  const handleSaveDomaine = (id: string) => {
    void runAction(async () => {
      await domaineMutations.updateDomaine(id, {
        libelle: editDomaine.libelle,
        slug: editDomaine.slug,
        intro: editDomaine.intro,
        urlImage: editDomaine.urlImage.trim() || null,
        icon: editDomaine.icon,
        ordre: Number(editDomaine.ordre) || 0,
        estActive: editDomaine.estActive,
      });
      setEditingDomaineId(null);
      return 'Domaine mis à jour';
    }, 'Impossible de mettre à jour le domaine');
  };

  const handleCreateFiliere = (e: React.FormEvent) => {
    e.preventDefault();
    void runAction(async () => {
      await filiereMutations.createFiliere({
        code: newFiliere.code,
        libelle: newFiliere.libelle,
        domaine: newFiliere.domaine,
        description: newFiliere.description || undefined,
        urlImage: newFiliere.urlImage || undefined,
        ordre: Number(newFiliere.ordre) || 0,
        contenuPedagogique: newFiliere.contenuPedagogique,
      });
      setShowFiliereForm(false);
      setNewFiliere({
        code: '',
        libelle: '',
        domaine: '',
        description: '',
        urlImage: '',
        ordre: '0',
        contenuPedagogique: emptyFiliereContenuPedagogique(),
        tarifs: emptyFiliereTarifsDraft(),
      });
      return 'Filière créée';
    }, 'Impossible de créer la filière');
  };

  const startEditFiliere = (f: Filiere) => {
    setShowFiliereForm(false);
    setEditingFiliereId(f.id);
    setEditFiliere({
      libelle: f.libelle || '',
      domaine: f.domaine || 'NUMERIQUE',
      description: f.description || '',
      urlImage: f.urlImage ?? '',
      ordre: String(f.ordre ?? 0),
      estActive: f.estActive !== false,
      contenuPedagogique: parseFiliereContenuPedagogique(f.contenuPedagogique) ?? emptyFiliereContenuPedagogique(),
      tarifs: emptyFiliereTarifsDraft(),
    });
    void (async () => {
      try {
        const data = await fetchAdminFiliereTarifs(f.id);
        setEditFiliere((prev) => ({
          ...prev,
          tarifs: buildFiliereTarifsDraftFromApi(data ?? {}),
        }));
      } catch {
        /* garde le brouillon vide / global */
      }
    })();
  };

  const handleSaveFiliere = (id: string) => {
    void runAction(async () => {
      await filiereMutations.updateFiliere(id, {
        libelle: editFiliere.libelle,
        domaine: editFiliere.domaine,
        description: editFiliere.description,
        urlImage: editFiliere.urlImage.trim() || null,
        ordre: Number(editFiliere.ordre) || 0,
        estActive: editFiliere.estActive,
        contenuPedagogique: editFiliere.contenuPedagogique,
      });
      await putAdminFiliereTarifs(id, filiereTarifsDraftToPayload(editFiliere.tarifs));
      setEditingFiliereId(null);
      return 'Filière mise à jour';
    }, 'Impossible de mettre à jour la filière');
  };

  const handleCreateSalle = (e: React.FormEvent) => {
    e.preventDefault();
    void runAction(async () => {
      await sallesState.createSalle({
        code: newSalle.code,
        libelle: newSalle.libelle || newSalle.code,
        capacite: Number(newSalle.capacite),
      });
      setShowSalleForm(false);
      setNewSalle({ code: '', libelle: '', capacite: '20' });
      return 'Salle créée';
    }, 'Impossible de créer la salle');
  };

  const startEditSalle = (s: Salle) => {
    setEditingSalleId(s.id);
    setEditSalle({
      libelle: s.libelle || '',
      capacite: String(s.capacite ?? 20),
      estActive: s.estActive !== false,
    });
  };

  const handleSaveSalle = (id: string) => {
    void runAction(async () => {
      await sallesState.saveSalle(id, {
        libelle: editSalle.libelle.trim() || undefined,
        capacite: Number(editSalle.capacite),
        estActive: editSalle.estActive,
      });
      setEditingSalleId(null);
      return 'Salle mise à jour';
    }, 'Impossible de mettre à jour la salle');
  };

  const combinedLoading = isLoading || sallesState.isLoading || domainesState.isLoading;
  const combinedError = isError || sallesState.isError || domainesState.isError;

  const inputClass = 'academia-field mt-1 w-full text-sm text-slate-900';

  return (
    <AdminPageShell
      title="Filières, cohortes et salles"
      sideCard={sideCard}
      description="Catalogue des parcours, sessions d'inscription et infrastructure des salles."
      onRetry={refreshAll}
      isLoading={combinedLoading}
      isError={combinedError}
      loadingMessage="Chargement des formations…"
      errorMessage="Impossible de charger les formations."
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <MiniStat label="Filières" value={String(filieres.length)} icon="school" />
        <MiniStat label="Cohortes" value={String(cohortes.length)} icon="groups" />
        <MiniStat label="Inscrits" value={String(totalInscrits)} icon="person" />
      </div>

      <div
        className="flex flex-wrap gap-2 rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-2"
        role="tablist"
        aria-label="Sections formations"
      >
        {(
          [
            { id: 'catalogue' as const, label: 'Catalogue filières', icon: 'menu_book' },
            { id: 'parcours' as const, label: 'Parcours et cohortes', icon: 'groups' },
            { id: 'domaines' as const, label: 'Domaines de formation', icon: 'folder' },
            { id: 'salles' as const, label: 'Salles et vacations', icon: 'location_city' },
          ] as const
        ).map((tab) => {
          const active = section === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                if (tab.id !== 'parcours') closeCohortesModal();
                setSection(tab.id);
              }}
              className={[
                'inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold transition sm:flex-none sm:min-w-[11rem] lg:min-w-[12.5rem]',
                active
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-slate-700 hover:bg-white',
              ].join(' ')}
            >
              <LucideSymbolIcon name={tab.icon} tone="inherit" size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {section === 'catalogue' ? (
        <section className="min-w-0 max-w-full space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-white p-5 shadow-sm">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Vitrine publique</p>
              <h2 className="mt-1 text-lg font-black text-slate-900">Catalogue filières</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
                {filieres.length} filière{filieres.length !== 1 ? 's' : ''} au total ·{' '}
                {filieres.filter((f) => f.estActive !== false).length} active
                {filieres.filter((f) => f.estActive !== false).length !== 1 ? 's' : ''} sur le site et l’app mobile
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (showFiliereForm) {
                  setEditingFiliereId(null);
                  setShowFiliereForm(false);
                  return;
                }
                setEditingFiliereId(null);
                setNewFiliere({
                  code: '',
                  libelle: '',
                  domaine: '',
                  description: '',
                  urlImage: '',
                  ordre: '0',
                  contenuPedagogique: emptyFiliereContenuPedagogique(),
                  tarifs: emptyFiliereTarifsDraft(),
                });
                setShowFiliereForm(true);
              }}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-primary/20 hover:opacity-95"
            >
              <LucideSymbolIcon
                name={showFiliereForm ? 'close' : 'add'}
                tone="inherit"
                size={18}
                className="text-white"
              />
              {showFiliereForm ? 'Fermer le formulaire' : 'Nouvelle filière'}
            </button>
          </div>

          {showFiliereForm ? (
            <FiliereCatalogForm
              mode="create"
              values={{ ...newFiliere, estActive: true }}
              domaineOptions={domaines.map((d) => ({ code: d.code, libelle: d.libelle }))}
              domaineEmpty={domaines.length === 0}
              onChange={(patch) => setNewFiliere((d) => ({ ...d, ...patch }))}
              onSubmit={(e) => void handleCreateFiliere(e)}
              isSaving={filiereMutations.isSaving}
              onUploadError={(message) => showToast(message, 'error')}
            />
          ) : null}

          <div className="min-w-0 space-y-4">
            {paginatedFilieres.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-outline-variant/25 bg-white px-6 py-12 text-center shadow-sm">
                <LucideSymbolIcon name="menu_book" tone="muted" size={40} className="mx-auto opacity-60" />
                <p className="mt-3 text-sm font-semibold text-slate-800">Aucune filière enregistrée</p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Ajoutez une filière pour alimenter le catalogue public et les inscriptions.
                </p>
              </div>
            ) : (
              <ul className="space-y-4">
                {paginatedFilieres.map((f) => (
                  <li key={f.id} className="min-w-0">
                    {editingFiliereId === f.id ? (
                      <FiliereCatalogForm
                        mode="edit"
                        values={{ ...editFiliere, code: f.code ?? '' }}
                        domaineOptions={domaines.map((d) => ({ code: d.code, libelle: d.libelle }))}
                        onChange={(patch) => setEditFiliere((x) => ({ ...x, ...patch }))}
                        onSubmit={(e) => {
                          e.preventDefault();
                          void handleSaveFiliere(f.id);
                        }}
                        onCancel={() => setEditingFiliereId(null)}
                        isSaving={filiereMutations.isSaving}
                        onUploadError={(message) => showToast(message, 'error')}
                      />
                    ) : (
                      <FiliereCatalogListItem
                        filiere={f}
                        domaineLabel={
                          domaineLabelByCode.get((f.domaine ?? '').toUpperCase()) ?? f.domaine ?? '—'
                        }
                        onEdit={() => startEditFiliere(f)}
                        deleteAction={
                          <AdminTableDeleteButton
                            canDelete={canDelete}
                            disabled={deletingEntityId === f.id}
                            confirmMessage={`Supprimer définitivement la filière « ${f.libelle || f.code} » ? Cette action est irréversible.`}
                            onDelete={async () => {
                              setDeletingEntityId(f.id);
                              try {
                                await runAction(async () => {
                                  await deleteAdminFiliere(f.id);
                                  onRefresh();
                                  return 'Filière supprimée.';
                                }, 'Suppression impossible.');
                              } finally {
                                setDeletingEntityId(null);
                              }
                            }}
                          />
                        }
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {filieres.length > 0 ? (
              <div className="rounded-2xl border border-outline-variant/15 bg-white shadow-sm">
                <AdminPagination
                  className="px-4 py-3"
                  currentPage={filierePage}
                  totalPages={filierePages}
                  onPageChange={onFilierePageChange}
                  summary={filieresPaginationSummary}
                />
              </div>
            ) : null}
          </div>
        </section>
      ) : section === 'parcours' ? (
        <>
          <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm">
            <div className="mb-5">
              <h2 className="text-lg font-black text-slate-900">Parcours et cohortes</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Cliquez sur un parcours pour ouvrir ses cohortes dans une fenêtre modale.
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-outline-variant/15 bg-white">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-outline-variant/15 bg-surface-container-low text-xs uppercase tracking-wide text-on-surface-variant">
                  <tr>
                    <th className="px-4 py-3">Parcours</th>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Domaine</th>
                    <th className="px-4 py-3">Cohortes</th>
                    <th className="px-4 py-3 text-right">Statut</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedParcoursRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-on-surface-variant">
                        Aucune filière configurée. Ajoutez-en une dans l’onglet Catalogue filières.
                      </td>
                    </tr>
                  ) : (
                    paginatedParcoursRows.map((f) => {
                      const count = countCohortesForFiliere(f.id, cohortes, f._count?.cohortes ?? 0);
                      const statuts = cohorteStatutsForFiliere(f.id, cohortes);
                      return (
                        <tr
                          key={f.id}
                          className="cursor-pointer border-b border-outline-variant/10 transition last:border-0 hover:bg-primary/5"
                          onClick={() => openCohortesModal(f.id)}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{f.libelle || '—'}</p>
                            {f.description ? (
                              <p
                                className="mt-0.5 max-w-md truncate text-xs text-on-surface-variant"
                                title={f.description}
                              >
                                {f.description}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-on-surface-variant">{f.code || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                              {domaineLabelByCode.get((f.domaine ?? '').toUpperCase()) ?? f.domaine ?? '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-800">{count}</td>
                          <td className="px-4 py-3 text-right">
                            {statuts.length === 0 ? (
                              <span className="text-xs text-on-surface-variant">—</span>
                            ) : (
                              <div className="flex flex-wrap justify-end gap-1">
                                {statuts.map((statut) => (
                                  <span
                                    key={statut}
                                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cohorteStatutClass(statut)}`}
                                  >
                                    {COHORTE_STATUT_LABELS_UI[statut] ?? statut}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <TableActionButton
                                icon="groups"
                                className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCohortesModal(f.id);
                                }}
                              >
                                Cohortes
                              </TableActionButton>
                              <span
                                className="inline-flex"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                role="presentation"
                              >
                                <AdminTableDeleteButton
                                  canDelete={canDelete}
                                  disabled={deletingEntityId === f.id}
                                  confirmMessage={`Supprimer définitivement le parcours « ${f.libelle || f.code} » et ses cohortes ? Cette action est irréversible.`}
                                  onDelete={async () => {
                                    setDeletingEntityId(f.id);
                                    try {
                                      await runAction(async () => {
                                        await deleteAdminFiliere(f.id);
                                        if (cohortesModalFiliereId === f.id) closeCohortesModal();
                                        onRefresh();
                                        return 'Parcours supprimé.';
                                      }, 'Suppression impossible.');
                                    } finally {
                                      setDeletingEntityId(null);
                                    }
                                  }}
                                />
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {filieres.length > 0 ? (
                <AdminPagination
                  className="border-t border-outline-variant/15 px-4 py-3"
                  currentPage={parcoursPage}
                  totalPages={parcoursPages}
                  onPageChange={onParcoursPageChange}
                  summary={parcoursPaginationSummary}
                />
              ) : null}
            </div>
          </section>

          {modalFiliere ? (
            <ParcoursCohortesModal
              filiere={modalFiliere}
              domaineLabel={
                domaineLabelByCode.get((modalFiliere.domaine ?? '').toUpperCase()) ?? modalFiliere.domaine ?? '—'
              }
              cohortes={cohortesForModal}
              onClose={closeCohortesModal}
              showCreate={showCreate}
              onToggleCreate={() => setShowCreate((v) => !v)}
              newCohorte={newCohorte}
              onNewCohorteChange={setNewCohorte}
              onCreate={(e) => void handleCreate(e)}
              onSuggestCapacite={() => void handleSuggestCapacite()}
              capSuggestion={capSuggestion}
              isSaving={isSaving}
              inputClass={inputClass}
              editingId={editingId}
              editDraft={editDraft}
              onStartEdit={startEdit}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={handleSaveEdit}
              onEditDraftChange={setEditDraft}
              renderCohorteDelete={(c) => (
                <AdminTableDeleteButton
                  canDelete={canDelete}
                  disabled={deletingEntityId === c.id}
                  confirmMessage={`Supprimer définitivement la cohorte « ${c.libelle || c.code} » ? Cette action est irréversible.`}
                  onDelete={async () => {
                    setDeletingEntityId(c.id);
                    try {
                      await runAction(async () => {
                        await deleteAdminCohorte(c.id);
                        if (modalFiliereId) await reloadCohortes(modalFiliereId);
                        onRefresh();
                        return 'Cohorte supprimée.';
                      }, 'Suppression impossible.');
                    } finally {
                      setDeletingEntityId(null);
                    }
                  }}
                />
              )}
            />
          ) : null}
        </>
      ) : section === 'domaines' ? (
        <section className="min-w-0 max-w-full space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-white p-5 shadow-sm">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Catalogue</p>
              <h2 className="mt-1 text-lg font-black text-slate-900">Domaines de formation</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
                Familles de filières (numériques, administratives, etc.). Chaque formation est rattachée à un
                domaine pour l’affichage vitrine web et mobile.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowDomaineForm((v) => !v);
                if (showDomaineForm) setEditingDomaineId(null);
              }}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-primary/20 hover:opacity-95"
            >
              <LucideSymbolIcon name={showDomaineForm ? 'close' : 'add'} tone="inherit" size={18} className="text-white" />
              {showDomaineForm ? 'Fermer le formulaire' : 'Nouveau domaine'}
            </button>
          </div>

          {showDomaineForm ? (
            <DomaineFormationForm
              mode="create"
              values={{ ...newDomaine, estActive: true }}
              onChange={(patch) =>
                setNewDomaine((d) => ({
                  ...d,
                  ...patch,
                  ...(patch.libelle != null
                    ? { slug: d.slug || slugifyDomaineLabel(patch.libelle) }
                    : {}),
                }))
              }
              onSubmit={(e) => void handleCreateDomaine(e)}
              isSaving={domaineMutations.isSaving}
              onUploadError={(message) => showToast(message, 'error')}
            />
          ) : null}

          <div className="min-w-0 space-y-4">
            {paginatedDomaines.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-outline-variant/25 bg-white px-6 py-12 text-center shadow-sm">
                <LucideSymbolIcon name="folder_open" tone="muted" size={40} className="mx-auto opacity-60" />
                <p className="mt-3 text-sm font-semibold text-slate-800">Aucun domaine enregistré</p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Créez votre premier domaine pour structurer le catalogue des formations.
                </p>
              </div>
            ) : (
              <ul className="space-y-4">
                {paginatedDomaines.map((d) => (
                  <li key={d.id} className="min-w-0">
                    {editingDomaineId === d.id ? (
                      <DomaineFormationForm
                        mode="edit"
                        values={{ ...editDomaine, code: d.code }}
                        onChange={(patch) => setEditDomaine((x) => ({ ...x, ...patch }))}
                        onSubmit={(e) => {
                          e.preventDefault();
                          void handleSaveDomaine(d.id);
                        }}
                        onCancel={() => setEditingDomaineId(null)}
                        isSaving={domaineMutations.isSaving}
                        onUploadError={(message) => showToast(message, 'error')}
                      />
                    ) : (
                      <DomaineFormationListItem
                        domaine={d}
                        onEdit={() => startEditDomaine(d)}
                        deleteAction={
                          <AdminTableDeleteButton
                            canDelete={canDelete}
                            disabled={deletingEntityId === d.id}
                            confirmMessage={`Supprimer définitivement le domaine « ${d.libelle} » ? Cette action est irréversible.`}
                            onDelete={async () => {
                              setDeletingEntityId(d.id);
                              try {
                                await runAction(async () => {
                                  await deleteAdminDomaine(d.id);
                                  await domainesState.refetch();
                                  return 'Domaine supprimé.';
                                }, 'Suppression impossible.');
                              } finally {
                                setDeletingEntityId(null);
                              }
                            }}
                          />
                        }
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {domaines.length > 0 ? (
              <div className="rounded-2xl border border-outline-variant/15 bg-white shadow-sm">
                <AdminPagination
                  className="px-4 py-3"
                  currentPage={domainePage}
                  totalPages={domainePages}
                  onPageChange={onDomainePageChange}
                  summary={domainesPaginationSummary}
                />
              </div>
            ) : null}
          </div>
        </section>
      ) : section === 'salles' ? (
        <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-black text-slate-900">Salles et vacations</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                {sallesState.salles.length} salle(s) · {sallesState.vacations.length} créneaux horaires (08h–20h)
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowSalleForm((v) => !v)}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white"
            >
              {showSalleForm ? 'Fermer' : 'Nouvelle salle'}
            </button>
          </div>
          {showSalleForm ? (
            <form
              onSubmit={(e) => void handleCreateSalle(e)}
              className="mt-4 grid gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:grid-cols-4"
            >
              <label className="block">
                <span className="text-xs font-semibold">Code *</span>
                <input
                  required
                  className={inputClass}
                  value={newSalle.code}
                  onChange={(e) => setNewSalle((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                  placeholder="SALLE_4"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Libellé</span>
                <input
                  className={inputClass}
                  value={newSalle.libelle}
                  onChange={(e) => setNewSalle((d) => ({ ...d, libelle: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Capacité *</span>
                <input
                  type="number"
                  min={1}
                  required
                  className={inputClass}
                  value={newSalle.capacite}
                  onChange={(e) => setNewSalle((d) => ({ ...d, capacite: e.target.value }))}
                />
              </label>
              <div className="flex items-end">
                <button type="submit" className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white">
                  Créer la salle
                </button>
              </div>
            </form>
          ) : null}
          <div className="mt-5 overflow-x-auto rounded-xl border border-outline-variant/15 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-outline-variant/15 bg-surface-container-low text-xs uppercase tracking-wide text-on-surface-variant">
                <tr>
                  <th className="px-4 py-3">Salle</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Capacité</th>
                  <th className="px-4 py-3">Sessions</th>
                  <th className="px-4 py-3 text-right">Statut</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSalles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-on-surface-variant">
                      Aucune salle enregistrée.
                    </td>
                  </tr>
                ) : (
                  paginatedSalles.map((s) =>
                    editingSalleId === s.id ? (
                      <tr key={s.id} className="border-b border-outline-variant/10 bg-surface-container-low/50">
                        <td colSpan={6} className="px-4 py-4">
                          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                            Modifier la salle ·{' '}
                            <span className="font-mono text-slate-700">{s.code || '—'}</span>
                          </p>
                          <div className="grid gap-3 sm:grid-cols-4">
                            <label className="block sm:col-span-2">
                              <span className="text-xs font-semibold text-on-surface-variant">Libellé</span>
                              <input
                                className={inputClass}
                                value={editSalle.libelle}
                                onChange={(e) => setEditSalle((x) => ({ ...x, libelle: e.target.value }))}
                              />
                            </label>
                            <label className="block">
                              <span className="text-xs font-semibold text-on-surface-variant">Capacité (places)</span>
                              <input
                                type="number"
                                min={1}
                                required
                                className={inputClass}
                                value={editSalle.capacite}
                                onChange={(e) => setEditSalle((x) => ({ ...x, capacite: e.target.value }))}
                              />
                            </label>
                            <label className="flex items-end gap-2 pb-2 text-xs font-semibold text-slate-700">
                              <input
                                type="checkbox"
                                checked={editSalle.estActive}
                                onChange={(e) => setEditSalle((x) => ({ ...x, estActive: e.target.checked }))}
                              />
                              Salle active
                            </label>
                            <div className="flex justify-end gap-2 sm:col-span-4">
                              <TableActionButton
                                icon="close"
                                className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                onClick={() => setEditingSalleId(null)}
                              >
                                Annuler
                              </TableActionButton>
                              <TableActionButton
                                icon="save"
                                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white hover:opacity-95"
                                onClick={() => void handleSaveSalle(s.id)}
                              >
                                Enregistrer
                              </TableActionButton>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={s.id} className="border-b border-outline-variant/10 last:border-0">
                        <td className="px-4 py-3 font-medium text-slate-900">{s.libelle || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-on-surface-variant">{s.code || '—'}</td>
                        <td className="px-4 py-3">{s.capacite ?? '—'} places</td>
                        <td className="px-4 py-3 text-on-surface-variant">{s._count?.sessions ?? 0}</td>
                        <td className="px-4 py-3 text-right">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              s.estActive !== false
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-red-50 text-red-800'
                            }`}
                          >
                            {s.estActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <TableActionButton
                              icon="edit"
                              className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/10"
                              onClick={() => startEditSalle(s)}
                            >
                              Modifier
                            </TableActionButton>
                            <TableActionButton
                              icon={s.estActive !== false ? 'block' : 'check_circle'}
                              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                                s.estActive !== false
                                  ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100'
                                  : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                              }`}
                              title={s.estActive !== false ? 'Désactiver la salle' : 'Activer la salle'}
                              onClick={() =>
                                void runAction(
                                  async () => {
                                    const nextActive = !(s.estActive !== false);
                                    await sallesState.saveSalle(s.id, { estActive: nextActive });
                                    return nextActive ? 'Salle activée' : 'Salle désactivée';
                                  },
                                  'Impossible de modifier le statut de la salle',
                                )
                              }
                            >
                              {s.estActive !== false ? 'Désactiver' : 'Activer'}
                            </TableActionButton>
                            <AdminTableDeleteButton
                              canDelete={canDelete}
                              disabled={deletingEntityId === s.id}
                              confirmMessage={`Supprimer définitivement la salle « ${s.libelle || s.code} » ? Cette action est irréversible.`}
                              onDelete={async () => {
                                setDeletingEntityId(s.id);
                                try {
                                  await runAction(async () => {
                                    await deleteAdminSalle(s.id);
                                    await sallesState.refetch();
                                    return 'Salle supprimée.';
                                  }, 'Suppression impossible.');
                                } finally {
                                  setDeletingEntityId(null);
                                }
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ),
                  )
                )}
              </tbody>
            </table>
            {sallesState.salles.length > 0 ? (
              <AdminPagination
                className="border-t border-outline-variant/15 px-4 py-3"
                currentPage={sallePage}
                totalPages={sallePages}
                onPageChange={onSallePageChange}
                summary={sallesSummary}
              />
            ) : null}
          </div>
        </section>
      ) : null}
    </AdminPageShell>
  );
}
